import { useEffect } from 'react';
import type { BoardDoc } from '../collab/BoardDoc';
import { useEditor } from '../state/store';
import { useCall } from './CallProvider';

/**
 * "Follow the presenter" — the call's only contribution to camera movement,
 * layered entirely on the follow mechanism `FollowBanner`/`useFollowPeer`
 * already implement. It does not introduce a second way to move the camera;
 * it just picks the target automatically instead of requiring everyone on
 * the call to click that peer's avatar by hand.
 *
 * Engaging only while nobody has already chosen a peer to follow — and only
 * auto-*disengaging* the peer it auto-picked — keeps a manual choice from
 * being silently overridden or silently dropped by this.
 */
export function useAutoFollowPresenter(doc: BoardDoc): void {
  const call = useCall();
  const inCall = call.status === 'connected' || call.status === 'reconnecting';

  useEffect(() => {
    if (!inCall) return;

    let autoFollowed: string | null = null;

    const apply = () => {
      const { followingUserId } = useEditor.getState();
      const presenter = doc.getPresence().find((p) => p.inCall && p.presenting);

      if (presenter && !followingUserId) {
        autoFollowed = presenter.userId;
        useEditor.getState().setUi({ followingUserId: presenter.userId });
        return;
      }
      // The presenter this hook picked stopped presenting or left — release
      // the camera rather than leaving it stuck on a frozen viewport. A peer
      // the user followed by hand is left alone even if they start presenting.
      if (!presenter && followingUserId && followingUserId === autoFollowed) {
        autoFollowed = null;
        useEditor.getState().setUi({ followingUserId: null });
      }
    };

    apply();
    return doc.subscribePresence(apply);
  }, [doc, inCall]);
}
